import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  Legend,
} from "recharts";
import PatientService from "../../services/PatientService";
import {
  format,
  parseISO,
  addMonths,
  startOfDay,
  endOfDay,
} from "date-fns";
import { useTranslation } from "react-i18next";
import { CCMPatient } from "../../interfaces/CCMPatient";

// Use a union type for duration choices.
type DurationOption = "1" | "3" | "6" | "12" | "fitAll";

interface MeasurementHistoryProps {
  patient: CCMPatient;
  conditionType: string;
  organization_id: string;
  token: string;
  open: boolean;
  onClose: () => void;
}

interface CachedData {
  history: any;
  planLimits: any;
}

type MeasurementDatum = { time: number } & Record<string, number | undefined>;

const BLOOD_PRESSURE_KEYS = ["systolic", "diastolic"];

// Helper: Given a measurement type and the patient, return the baseline value if available.
const getBaselineForMeasurement = (
  measurementType: string,
  patient: CCMPatient
): number | null => {
  const type = measurementType.toLowerCase();
  if (type.includes("oxygen_saturation") && patient.baseline_spo2 != null) {
    return patient.baseline_spo2;
  }
  if (type.includes("weight") && patient.baseline_weight != null) {
    return patient.baseline_weight;
  }
  if (type.includes("systolic") && patient.baseline_systolic != null) {
    return patient.baseline_systolic;
  }
  if (type.includes("diastolic") && patient.baseline_diastolic != null) {
    return patient.baseline_diastolic;
  }
  if (type.includes("fev1") && patient.baseline_fev1 != null) {
    return patient.baseline_fev1;
  }
  if (type.includes("heart") && patient.baseline_hr != null) {
    return patient.baseline_hr;
  }
  if (type.includes("glucose") && patient.baseline_glucose != null) {
    return patient.baseline_glucose;
  }
  return null;
};

export const MeasurementTrendDialog: React.FC<MeasurementHistoryProps> = ({
  patient,
  conditionType,
  organization_id,
  token,
  open,
  onClose,
}) => {
  const [loading, setLoading] = useState(true);
  const [historyData, setHistoryData] = useState<any>(null);
  const [planLimits, setPlanLimits] = useState<any>(null);
  const { t } = useTranslation();

  // Selected duration as a union type. Default is "1" month.
  const [selectedDuration, setSelectedDuration] =
    useState<DurationOption>("1");

  // Cache measurement data per duration choice.
  const cacheRef = useRef<{ [key: string]: CachedData }>({});

  // Compute the API range.
  const startForAPI =
    selectedDuration === "fitAll"
      ? "1900-01-01T00:00"
      : format(
          startOfDay(addMonths(new Date(), -Number(selectedDuration))),
          "yyyy-MM-dd'T'HH:mm"
        );
  const endForAPI = format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm");


  // Fetch measurement history when dialog opens or when selectedDuration changes.
  useEffect(() => {
    if (open) {
      fetchMeasurementHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedDuration]);

  const fetchMeasurementHistory = async () => {
    setLoading(true);
    const cacheKey = `duration-${selectedDuration}`;
    if (cacheRef.current[cacheKey]) {
      const cached = cacheRef.current[cacheKey];
      setHistoryData(cached.history);
      setPlanLimits(cached.planLimits);
      setLoading(false);
      return;
    }
    try {
      const response = await PatientService.getMeasurementHistory(
        token,
        patient.patient_id,
        organization_id,
        startForAPI,
        endForAPI
      );
      setHistoryData(response.history);
      setPlanLimits(response.planlimits);
      cacheRef.current[cacheKey] = {
        history: response.history,
        planLimits: response.planlimits,
      };
    } catch (error) {
      console.error("Error fetching measurement history:", error);
    }
    setLoading(false);
  };

  // Compute the X-axis domain.
  let xDomain: [number, number];
  if (selectedDuration === "fitAll" && historyData) {
    const allTimestamps: number[] = [];
    Object.values(historyData).forEach((observations: any) => {
      observations.forEach(([timestamp]: [string, number]) => {
        allTimestamps.push(parseISO(timestamp).getTime());
      });
    });
    if (allTimestamps.length > 0) {
      xDomain = [Math.min(...allTimestamps), Math.max(...allTimestamps)];
    } else {
      xDomain = [startOfDay(new Date()).getTime(), endOfDay(new Date()).getTime()];
    }
  } else {
    const startFixed = startOfDay(
      addMonths(new Date(), -Number(selectedDuration))
    ).getTime();
    const endFixed = endOfDay(new Date()).getTime();
    xDomain = [startFixed, endFixed];
  }

  // Helper functions for thresholds.
  function getYAxisDomain(measurementType: string) {
    const limits = planLimits?.[measurementType]?.range;
    return limits ? [limits.low, limits.high] : ["auto", "auto"];
  }
  function getMaxLimit(measurementType: string) {
    return planLimits?.[measurementType]?.range?.high || null;
  }
  function getCriticalHigh(measurementType: string) {
    return planLimits?.[measurementType]?.high?.critical || null;
  }
  function getMajorHigh(measurementType: string) {
    return planLimits?.[measurementType]?.high?.major || null;
  }
  function getMinorHigh(measurementType: string) {
    return planLimits?.[measurementType]?.high?.minor || null;
  }
  function getMinLimit(measurementType: string) {
    return planLimits?.[measurementType]?.range?.low || null;
  }
  function getCriticalLow(measurementType: string) {
    return planLimits?.[measurementType]?.low?.critical || null;
  }
  function getMajorLow(measurementType: string) {
    return planLimits?.[measurementType]?.low?.major || null;
  }
  function getMinorLow(measurementType: string) {
    return planLimits?.[measurementType]?.low?.minor || null;
  }

  const getPanelDomain = (
    measurementTypes: string[]
  ): [number | "auto", number | "auto"] => {
    const lows: number[] = [];
    const highs: number[] = [];
    measurementTypes.forEach((type) => {
      const range = planLimits?.[type]?.range;
      if (range?.low != null) {
        lows.push(range.low);
      }
      if (range?.high != null) {
        highs.push(range.high);
      }
    });
    if (!lows.length || !highs.length) {
      return ["auto", "auto"] as [number | "auto", number | "auto"];
    }
    return [Math.min(...lows), Math.max(...highs)];
  };

  const getLineColor = (measurementType: string) => {
    switch (measurementType) {
      case "systolic":
        return "#8884d8";
      case "diastolic":
        return "#82ca9d";
      default:
        return "#8884d8";
    }
  };

  const renderThresholdAreas = (measurementType: string) => {
    const segments = [
      {
        key: "max-criticalHigh",
        y1: getMaxLimit(measurementType),
        y2: getCriticalHigh(measurementType),
        fill: "red",
      },
      {
        key: "criticalHigh-majorHigh",
        y1: getCriticalHigh(measurementType),
        y2: getMajorHigh(measurementType),
        fill: "orange",
      },
      {
        key: "majorHigh-minorHigh",
        y1: getMajorHigh(measurementType),
        y2: getMinorHigh(measurementType),
        fill: "yellow",
      },
      {
        key: "minorLow-majorLow",
        y1: getMinorLow(measurementType),
        y2: getMajorLow(measurementType),
        fill: "yellow",
      },
      {
        key: "majorLow-criticalLow",
        y1: getMajorLow(measurementType),
        y2: getCriticalLow(measurementType),
        fill: "orange",
      },
      {
        key: "criticalLow-minLimit",
        y1: getCriticalLow(measurementType),
        y2: getMinLimit(measurementType),
        fill: "red",
      },
    ];

    return segments
      .filter(
        ({ y1, y2 }) =>
          typeof y1 === "number" &&
          typeof y2 === "number" &&
          // Skip zero-height areas
          y1 !== y2
      )
      .map(({ key, y1, y2, fill }) => (
        <ReferenceArea
          key={`${measurementType}-${key}`}
          y1={y1 as number}
          y2={y2 as number}
          fill={fill}
          fillOpacity={0.2}
        />
      ));
  };

  interface MeasurementPanel {
    key: string;
    measurementTypes: string[];
    data: MeasurementDatum[];
  }

  const measurementPanels = React.useMemo<MeasurementPanel[]>(() => {
    if (!historyData) {
      return [];
    }
    const buildChartData = (
      measurementTypes: string[]
    ): MeasurementDatum[] => {
      const dataByTime = new Map<number, MeasurementDatum>();
      measurementTypes.forEach((type) => {
        const entries = historyData?.[type] || [];
        entries.forEach(([timestamp, value]: [string, number]) => {
          const time = parseISO(timestamp).getTime();
          if (!dataByTime.has(time)) {
            dataByTime.set(time, { time } as MeasurementDatum);
          }
          const existing = dataByTime.get(time);
          if (existing) {
            existing[type] = value;
          }
        });
      });
      return Array.from(dataByTime.values()).sort((a, b) => a.time - b.time);
    };
    const panels: MeasurementPanel[] = [];
    const seen = new Set<string>();

    Object.keys(historyData).forEach((measurementType) => {
      if (seen.has(measurementType)) {
        return;
      }
      if (BLOOD_PRESSURE_KEYS.includes(measurementType)) {
        const availableBpTypes = BLOOD_PRESSURE_KEYS.filter(
          (type) => historyData?.[type]
        );
        if (availableBpTypes.length) {
          panels.push({
            key: "blood_pressure",
            measurementTypes: availableBpTypes,
            data: buildChartData(availableBpTypes),
          });
          availableBpTypes.forEach((type) => seen.add(type));
        }
      } else {
        panels.push({
          key: measurementType,
          measurementTypes: [measurementType],
          data: buildChartData([measurementType]),
        });
        seen.add(measurementType);
      }
    });

    return panels;
  }, [historyData]);
  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography variant="h6">{t("Vital Parameter Graph")} - {patient.patient_id}</Typography>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography variant="subtitle1">{t("Select Duration")}</Typography>
            <RadioGroup
              row
              value={selectedDuration}
              onChange={(e) =>
                setSelectedDuration(e.target.value as DurationOption)
              }
            >
              <FormControlLabel value="1" control={<Radio />} label={t("1 Month")} />
              <FormControlLabel value="3" control={<Radio />} label={t("3 Months")} />
              <FormControlLabel value="6" control={<Radio />} label={t("6 Months")} />
              <FormControlLabel value="12" control={<Radio />} label={t("1 Year")} />
              <FormControlLabel value="fitAll" control={<Radio />} label={t("Fit All")} />
            </RadioGroup>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={100}>
            <CircularProgress />
          </Box>
        ) : historyData && Object.keys(historyData).length > 0 ? (
          <Box>
            {measurementPanels.map((panel) => {
              const panelTitle =
                panel.key === "blood_pressure"
                  ? t("blood_pressure")
                  : t(panel.measurementTypes[0]);
              const yAxisDomain =
                panel.measurementTypes.length === 1
                  ? getYAxisDomain(panel.measurementTypes[0])
                  : getPanelDomain(panel.measurementTypes);
              return (
                <Paper
                  key={panel.key}
                  sx={{ padding: 1, marginBottom: 2, backgroundColor: "#FAFAFA" }}
                >
                  <Typography
                    variant="h6"
                    sx={{ marginTop: 0, marginBottom: 0, color: "#222" }}
                  >
                    {panelTitle}
                  </Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart
                      data={panel.data}
                      margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
                      style={{ backgroundColor: "#FAFAFA" }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        domain={xDomain}
                        tickFormatter={(tick) =>
                          format(new Date(tick), "MMM dd HH:mm")
                        }
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis
                        domain={yAxisDomain as [number | "auto", number | "auto"]}
                        tick={{ fontSize: 10 }}
                      />
                      <ChartTooltip
                        labelFormatter={(label) =>
                          format(new Date(label), "MMM dd HH:mm")
                        }
                        formatter={(value: any, name: any) => [
                          value,
                          typeof name === "string" ? t(name) : name,
                        ]}
                      />
                      <Legend 
                        verticalAlign="bottom" 
                        height={36}
                        formatter={(value: string) => t(value)}
                      />
                      {panel.measurementTypes.flatMap((measurementType) =>
                        renderThresholdAreas(measurementType)
                      )}
                      {/* Render baseline(s) if available */}
                      {panel.measurementTypes.map((measurementType) => {
                        const baseline =
                          patient &&
                          getBaselineForMeasurement(measurementType, patient);
                        if (baseline === null || baseline === undefined) {
                          return null;
                        }
                        return (
                          <ReferenceLine
                            key={`${panel.key}-${measurementType}-baseline`}
                            y={baseline}
                            stroke="darkgreen"
                            strokeDasharray="5 5"
                            strokeWidth={3}
                          />
                        );
                      })}
                      {panel.measurementTypes.map((measurementType) => (
                        <Line
                          key={`${panel.key}-${measurementType}`}
                          type="monotone"
                          isAnimationActive={false}
                          dataKey={measurementType}
                          stroke={getLineColor(measurementType)}
                          strokeWidth={2}
                          dot={{ r: 2 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Typography
            variant="h6"
            sx={{ marginTop: 2, textAlign: "center" }}
          >
            {t("No Measurements Found")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
